// 警報工作台（EVT-07，SPEC #vms-web-alarm-workbench，app/vms#18）的純邏輯：版型斷點、接手時限的顯示、篩選、
// 處理完自動到下一則、按鍵對照。畫面在 features/events/AlarmWorkbench.tsx。
import type { AlarmChange, AlarmInfo, AlarmSla, AlarmStateName } from './alarms';

/**
 * 工作台能按的狀態動作（照 EVT-06 的轉換規則，不是事件列〔處理 ▾〕的精簡清單）：還沒處理的可以直接〔已處理〕（R 鍵），
 * 別人處理中可以接手，自己處理中只能結案；結案的只有〔重開〕。
 */
export function workbenchActions(info: AlarmInfo | undefined, me: string): AlarmStateName[] {
  switch (info?.state ?? 'open') {
    case 'open':
      return ['acknowledged', 'claimed', 'resolved', 'false_alarm'];
    case 'acknowledged':
      return ['claimed', 'resolved', 'false_alarm'];
    case 'claimed':
      return info?.claimed_by && info.claimed_by !== me ? ['claimed', 'resolved', 'false_alarm'] : ['resolved', 'false_alarm'];
    default:
      return ['open'];
  }
}

/** 版型（看瀏覽器視窗寬度）：≥ 1280 左右分割、768–1279 清單＋抽屜、< 768 手機照舊整頁清單。 */
export type WorkbenchMode = 'split' | 'drawer' | 'mobile';
export const WB_SPLIT_MIN = 1280;
export const WB_DRAWER_MIN = 768;

export function workbenchMode(width: number): WorkbenchMode {
  if (width >= WB_SPLIT_MIN) return 'split';
  if (width >= WB_DRAWER_MIN) return 'drawer';
  return 'mobile';
}

type T = (k: string, o?: Record<string, unknown>) => string;

/** 「12 分」「2 小時」「1 天」（無條件捨去；不到 1 分鐘算 0 分）。 */
export function durText(t: T, ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60_000));
  if (m < 60) return t('wb.dur_min', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('wb.dur_hour', { count: h });
  return t('wb.dur_day', { count: Math.floor(h / 24) });
}

export type SlaView =
  | { kind: 'running'; dueMs: number; leftMs: number; tone: 'ok' | 'warn' }
  | { kind: 'over'; dueMs: number; overMs: number }
  | { kind: 'recovered' }
  | { kind: 'stopped' };

/** 還沒接手（open / acknowledged / 不知道）才計時；接手 / 結案就停。 */
export function slaRunning(state: AlarmStateName | undefined): boolean {
  return state === undefined || state === 'open' || state === 'acknowledged';
}

/**
 * 接手時限的狀態（SPEC「處理時限 = 接手時限」）：還沒接手 → 剩多少（剩不到 5 分鐘或不到時限的 25% 改橘）/ 逾時多久；
 * 已恢復的狀態類 → 不計時；接手 / 結案 → 停。沒有時限 → null。
 */
export function slaView(sla: AlarmSla | undefined, state: AlarmStateName | undefined, recovered: boolean, now: number): SlaView | null {
  if (!sla) return null;
  if (!slaRunning(state)) return { kind: 'stopped' };
  if (recovered) return { kind: 'recovered' };
  const due = Date.parse(sla.due_at);
  if (!Number.isFinite(due)) return null;
  const left = due - now;
  if (left <= 0) return { kind: 'over', dueMs: due, overMs: -left };
  const warn = left < 5 * 60_000 || left < sla.minutes * 60_000 * 0.25;
  return { kind: 'running', dueMs: due, leftMs: left, tone: warn ? 'warn' : 'ok' };
}

/** 時限膠囊的字：「剩 12 分」「逾時 3 分」「已逾時」（過期不到 1 分鐘）；已恢復 / 停了 → 空（左欄不畫）。 */
export function slaChipText(t: T, v: SlaView | null): string {
  if (!v) return '';
  if (v.kind === 'running') return t('wb.sla_left', { d: durText(t, v.leftMs) });
  if (v.kind === 'over') return v.overMs < 60_000 ? t('wb.sla_over_now') : t('wb.sla_over', { d: durText(t, v.overMs) });
  return '';
}

/** 接手 / 結案的時刻（歷程裡起算之後第一筆變成 claimed / resolved / false_alarm 的狀態變更）；沒有 → null。 */
export function slaMetAt(history: AlarmChange[], startedAt: string): number | null {
  const from = Date.parse(startedAt);
  for (const h of history) {
    if ((h.kind ?? 'state') !== 'state') continue;
    const at = Date.parse(h.at);
    if (at >= from && (h.to === 'claimed' || h.to === 'resolved' || h.to === 'false_alarm')) return at;
  }
  return null;
}

/** 右欄的時限說明：「12 分鐘內接手」「超過時限 3 分才接手」。 */
export function slaMetText(t: T, sla: AlarmSla, metAt: number): string {
  const took = metAt - Date.parse(sla.started_at);
  const late = metAt - Date.parse(sla.due_at);
  return late > 0 ? t('wb.sla_met_late', { d: durText(t, late) }) : t('wb.sla_met', { d: durText(t, took) });
}

/** 左欄篩選（單選）：全部 / 指派給我（還沒處理、指派給我）/ 逾時（還沒接手、過了時限、不是已恢復與未登記）。 */
export type WbFilter = 'all' | 'mine' | 'overdue';
export const WB_FILTERS: WbFilter[] = ['all', 'mine', 'overdue'];

export interface WbRowFacts {
  state: AlarmStateName | undefined;
  info?: AlarmInfo;
  sla?: AlarmSla;
  recovered: boolean;
  listed: boolean;
}

export function isMine(f: WbRowFacts, me: string): boolean {
  return !!me && slaRunning(f.state) && f.info?.assigned_to === me;
}

export function isOverdue(f: WbRowFacts, now: number): boolean {
  return f.listed && slaView(f.sla, f.state, f.recovered, now)?.kind === 'over';
}

export function passFilter(filter: WbFilter, f: WbRowFacts, me: string, now: number): boolean {
  if (filter === 'mine') return isMine(f, me);
  if (filter === 'overdue') return isOverdue(f, now);
  return true;
}

/**
 * 處理完自動到下一則（SPEC「處理完自動到下一則」）：原本排在它下面、還沒處理（警報整理的組別 ①–③）的第一則；
 * 下面沒有就從頭找；都沒有 → null（右欄寫「全部處理完了」）。rows 是處理前的順序。
 */
export function nextUnhandled(rows: { id: string; unhandled: boolean }[], currentId: string): string | null {
  const i = rows.findIndex((r) => r.id === currentId);
  const after = rows.slice(i + 1).find((r) => r.unhandled && r.id !== currentId);
  if (after) return after.id;
  return rows.slice(0, Math.max(0, i)).find((r) => r.unhandled && r.id !== currentId)?.id ?? null;
}

/** 選取那則不見了（被篩掉 / 刪掉）→ 原本位置的下一列（沒有就上一列）。 */
export function keepSelection(prev: string[], next: string[], sel: string | null): string | null {
  if (!next.length) return null;
  if (sel && next.includes(sel)) return sel;
  const i = sel ? prev.indexOf(sel) : -1;
  for (let k = i + 1; k < prev.length; k++) if (next.includes(prev[k])) return prev[k];
  for (let k = i - 1; k >= 0; k--) if (next.includes(prev[k])) return prev[k];
  return next[0];
}

/** 工作台的按鍵（SPEC 鍵盤表）。按著 ⌘ / Ctrl / Alt 一律不攔截；Shift 只認「?」。 */
export type WbKey = 'next' | 'prev' | 'ack' | 'claim' | 'resolve' | 'false' | 'assign' | 'note' | 'enlarge' | 'wave' | 'help';

export function wbKeyOf(e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>): WbKey | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.key === '?') return 'help';
  if (e.shiftKey) return null;
  switch (e.key) {
    case 'j':
    case 'J':
    case 'ArrowDown':
      return 'next';
    case 'k':
    case 'K':
    case 'ArrowUp':
      return 'prev';
    case 'Enter':
      return 'enlarge';
  }
  switch (e.key.toLowerCase()) {
    case 'a':
      return 'ack';
    case 'c':
      return 'claim';
    case 'r':
      return 'resolve';
    case 'f':
      return 'false';
    case 's':
      return 'assign';
    case 'n':
      return 'note';
    case 'x':
      return 'wave';
  }
  return null;
}

/** 狀態動作鍵 → 要改成的狀態。 */
export const KEY_STATE: Partial<Record<WbKey, AlarmStateName>> = { ack: 'acknowledged', claim: 'claimed', resolve: 'resolved', false: 'false_alarm' };

/** 按鈕上的快捷鍵字母。 */
export const STATE_KEY: Partial<Record<AlarmStateName, string>> = { acknowledged: 'A', claimed: 'C', resolved: 'R', false_alarm: 'F' };
